import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  InputAdornment,
} from "@mui/material";
import { createClientVersion } from "../../../../Services/ClintsApi";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IconButton from "@mui/material/IconButton";

const CreateClientVersionDialog = ({ open, onClose, customer }) => {
  const [newVersion, setNewVersion] = useState({
    domain: "",
    user_id: "",
    user_name: "",
    is_demo: 0,
  });
  const [loading, setLoading] = useState(false);

  // ✅ تعبئة القيم تلقائياً عند فتح النافذة
  useEffect(() => {
    if (customer) {
      setNewVersion({
        domain: customer.domain || "",
        user_id: customer.id || "",
        user_name: customer.name || "",
        is_demo: 0, // 0 = نسخة مدفوعة، 1 = تجريبية
      });
    }
  }, [customer]);

  const handleChange = (field, value) => {
    setNewVersion((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      const res = await createClientVersion(newVersion);
      alert("✅ تم إنشاء النسخة بنجاح!");
      console.log("API Response:", res.data);
      onClose(true); // ✅ نرسل true لنعلم المكون الأب بنجاح العملية
    } catch (err) {
      console.error("❌ خطأ أثناء إنشاء النسخة:", err);
      alert("❌ فشل إنشاء النسخة، تحقق من البيانات أو السيرفر");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#9c27b0",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "white", color: "#9c27b0" }}>
            <FileCopyIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              إنشاء نسخة جديدة للعميل
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              إنشاء نسخة جديدة من النظام للعميل
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => onClose(false)}
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {customer && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              bgcolor: "#f3e5f5",
              border: "2px solid #e1bee7",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#9c27b0",
                    width: 40,
                    height: 40,
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: "bold", color: "#9c27b0" }}
                  >
                    معلومات العميل
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#9c27b0" }}
                  >
                    {customer.name}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={`ID: ${customer.id}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                {customer.email && (
                  <Chip
                    label={customer.email}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        <Card variant="outlined" sx={{ bgcolor: "#fafafa" }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <LanguageIcon sx={{ color: "#9c27b0", fontSize: 24 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", color: "#333" }}
              >
                عنوان النطاق (Domain)
              </Typography>
              <Chip label="مطلوب" size="small" color="error" />
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="أدخل عنوان النطاق (مثال: example.com)"
              value={newVersion.domain}
              onChange={(e) => handleChange("domain", e.target.value)}
              sx={{ bgcolor: "white" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon sx={{ color: "#9c27b0" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              أدخل اسم النطاق الذي سيتم ربط النسخة به
            </Typography>
          </CardContent>
        </Card>

        {/* ✅ الحقول المخفية */}
        <input type="hidden" value={newVersion.user_id} />
        <input type="hidden" value={newVersion.is_demo} />
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          size="large"
          sx={{ minWidth: 100 }}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          size="large"
          disabled={loading || !newVersion.domain}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
          sx={{
            minWidth: 120,
            fontWeight: "bold",
            background:
              "linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)",
            "&:hover": {
              background:
                "linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)",
            },
            "&:disabled": {
              background: "#e0e0e0",
            },
          }}
        >
          {loading ? "جاري الإنشاء..." : "إنشاء النسخة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClientVersionDialog;
